// src/App.js
import React, { useEffect, useState } from "react";
import {
  Routes, Route, useNavigate, useLocation,
} from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { loadStripe } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

import Signup from "./pages/Signup";
import Login from "./pages/Login";
import CustomerDashboard from "./pages/CustomerDashboard";
import Payment from "./pages/Payment";
import Welcome from "./pages/Welcome";
import CompleteProfile from "./pages/CompleteProfile";
import VerifyPhone from "./pages/VerifyPhone";
import OTPVerification from "./pages/OTPVerification";
import SetAddress from "./pages/SetAddress";
import Profile from "./pages/Profile";
import YourJobs from "./pages/YourJobs";
import PrivateRoute from "./components/PrivateRoute";
import Layout from "./components/Layout";
import Help from "./pages/Help";
import JobPostedSuccess from "./pages/JobPostedSuccess";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import IntroAnimation from "./pages/IntroAnimation";
import JobDetails from "./pages/JobDetails";
import Chat from "./pages/Chat";
import SupportChat from "./pages/SupportChat";
import WorkerPublicView from "./pages/WorkerPublicView";
import CustomerRideSearch from "./pages/CustomerRideSearch";
import CustomerRideResults from "./pages/CustomerRideResults";
import PostRideRequest from "./pages/PostRideRequest";
import CustomerRidePreview from "./pages/CustomerRidePreview";
import RideChatPage from "./pages/RideChatPage";
import CustomerWallet from "./pages/CustomerWallet";
import PaymentSuccess from "./pages/PaymentSuccess";
import NotificationsPage from "./pages/NotificationsPage";
import TermsAndConditions from "./pages/TermsAndConditions";
import UploadProfilePicture from "./pages/UploadProfilePicture";
import ChangePassword from "./pages/ChangePassword";
import BidAccepted from "./pages/BidAccepted";
import RefundReceipt from "./pages/RefundReceipt";
import NoWorkersPage from "./pages/NoWorkersPage";
import YourTrips from "./pages/YourTrips";
import RidePaymentPage from "./pages/RidePaymentPage";
import LiveChat from "./pages/LiveChat";

import "./App.css";
import "./pages/IntroAnimation.css";

const API_CUST = process.env.REACT_APP_API_CUST;

let stripePromise = null;
const rawKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
if (rawKey?.startsWith("pk_")) {
  stripePromise = loadStripe(rawKey);
} else {
  console.error("❌ Stripe key missing or invalid.");
}

function App() {
  const [profile, setProfile] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const publicRoutes = ["/", "/welcome", "/login", "/signup", "/forgot-password", "/reset-password", "/terms", "/customer-dashboard"];

    const checkAuth = async () => {
      if (publicRoutes.includes(location.pathname)) return;

      const email = localStorage.getItem("userEmail");
      const token = localStorage.getItem("userToken");

      if (!email || !token) {
        localStorage.clear();
        return navigate("/login");
      }

      try {
        const res = await fetch(`${API_CUST}/user-profile/${email}`);
        const data = await res.json();

        if (!data || data.error) {
          localStorage.clear();
          return navigate("/login");
        }

        setProfile(data);
        localStorage.setItem("userName", `${data.name || ""} ${data.lastName || ""}`.trim());
        localStorage.setItem("userAddress", data.address || "");
        localStorage.setItem("userPhone", data.phone || "");

        if (!data.name || !data.lastName) return navigate("/complete-profile", { replace: true });
        if (!data.phone) return navigate("/verify-phone", { replace: true });
        if (!data.address) return navigate("/set-address", { replace: true });
        if (!data.profilePicture && !data.profilePictureSkipped) {
          return navigate("/upload-photo", { replace: true });
        }
        if (!data.termsAccepted) return navigate("/terms", { replace: true });

        const blockedRoutes = [
          "/login", "/signup", "/complete-profile", "/verify-phone", "/set-address", "/upload-photo", "/terms"
        ];
        if (blockedRoutes.includes(location.pathname)) {
          navigate("/welcome", { replace: true });
        }
      } catch (err) {
        console.error("❌ Session check failed:", err);
        localStorage.clear();
        return navigate("/login");
      }
    };

    checkAuth();
  }, [location.pathname, navigate]);

  const handleLogout = () => {
    localStorage.clear();
    setProfile(null);
    navigate("/login");
  };

  // ✅ Detect pages where navbars should be hidden
  const hideNavRoutes = [
    "/", "/login", "/signup", "/forgot-password", "/reset-password", "/complete-profile",
    "/verify-phone", "/set-address", "/upload-photo", "/terms"
  ];
  const hideLayout = hideNavRoutes.includes(location.pathname);

return (
  <div className="min-h-screen bg-gray-100 dark:bg-gray-900">
    {stripePromise ? (
      <Elements stripe={stripePromise}>
        {/* ✅ AnimatePresence ONLY wraps Routes */}
        <AnimatePresence mode="wait">
          {hideLayout ? (
            <Routes location={location} key={location.pathname}>
              <Route path="/" element={<IntroAnimation />} />
              <Route path="/signup" element={<Signup />} />
              <Route path="/login" element={<Login />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/terms" element={<TermsAndConditions />} />


              {/* ✅ these all should be here */}
    <Route path="/complete-profile" element={<CompleteProfile />} />
    <Route path="/verify-phone" element={<PrivateRoute><VerifyPhone /></PrivateRoute>} />
    <Route path="/otp-verification" element={<PrivateRoute><OTPVerification /></PrivateRoute>} />
    <Route path="/set-address" element={<PrivateRoute><SetAddress /></PrivateRoute>} />
    <Route path="/upload-photo" element={<PrivateRoute><UploadProfilePicture /></PrivateRoute>} />
            </Routes>
          ) : (
              <Layout>
  <Routes location={location} key={location.pathname}>
                <Route path="/welcome" element={<Welcome />} />
                <Route path="/customer-dashboard" element={<CustomerDashboard />} />
                <Route path="/ride-request" element={<CustomerRideSearch />} />
                <Route path="/customer/ride-results" element={<CustomerRideResults />} />
                <Route path="/customer/ride-preview/:rideId" element={<CustomerRidePreview />} />
                <Route path="/worker-profile/:workerId" element={<WorkerPublicView />} />
                <Route path="/no-workers" element={<NoWorkersPage />} />
                <Route path="/payment" element={<PrivateRoute><Payment /></PrivateRoute>} />
                <Route path="/payment-success" element={<PrivateRoute><PaymentSuccess /></PrivateRoute>} />
                <Route path="/wallet" element={<PrivateRoute><CustomerWallet /></PrivateRoute>} />
                <Route path="/notifications" element={<PrivateRoute><NotificationsPage /></PrivateRoute>} />
                <Route path="/profile" element={<PrivateRoute><Profile /></PrivateRoute>} />
                <Route path="/help" element={<PrivateRoute><Help /></PrivateRoute>} />
                <Route path="/live-chat" element={<LiveChat />} />
                <Route path="/your-jobs" element={<PrivateRoute><YourJobs /></PrivateRoute>} />
                <Route path="/job-posted-success" element={<PrivateRoute><JobPostedSuccess /></PrivateRoute>} />
                <Route path="/job/:jobId" element={<PrivateRoute><JobDetails /></PrivateRoute>} />
                <Route path="/chat/:sender/:receiver/:jobId" element={<PrivateRoute><Chat /></PrivateRoute>} />
                <Route path="/support-chat/:email" element={<PrivateRoute><SupportChat /></PrivateRoute>} />
                <Route path="/change-password" element={<PrivateRoute><ChangePassword /></PrivateRoute>} />
                <Route path="/customer/post-request" element={<PrivateRoute><PostRideRequest /></PrivateRoute>} />
                <Route
  path="/customer/ride-chat/:rideId"
  element={
    <PrivateRoute>
      <RideChatPage />
    </PrivateRoute>
  }
/>

                <Route path="/bid-accepted" element={<BidAccepted />} />
                <Route path="/refund-receipt/:jobId" element={<RefundReceipt />} />
                <Route path="/your-trips" element={<YourTrips />} />
                <Route path="/ride-payment" element={<RidePaymentPage />} />
                <Route path="*" element={<h1 className="text-center text-4xl font-bold mt-10">Page Not Found</h1>} />
              </Routes>
            </Layout>
          )}
        </AnimatePresence>
      </Elements>
    ) : (
      <div className="text-center text-red-600 font-bold text-xl p-10">
        ⚠️ Stripe initialization failed. Please check your Stripe key in .env file.
      </div>
    )}
  </div>
);

}

export default App;
